import { useSearchParams } from "react-router-dom"

import { useToast } from "@hooks"
import { settingsLeaveGuards, userSettingsTab } from "../@signals"


// The URL as it is when the answer arrives (a confirm can be answered seconds after it was asked)
const searchWith = (tab) => {
	const params = new URLSearchParams(window.location.search)
	params.set('tab', tab)
	return params
}

/**
 * Changing the Settings tab, from a click, the URL or the guided tour: a tab with unsaved changes asks
 * first ("Leave without saving?" · Stay / Leave, UX D7). Leaving never saves. `then` runs once the tab
 * changed; `onStay` when the user stays (or the question closes unanswered).
 */
export default function useUserSettingsTabs() {
	const { showToast } = useToast()
	const [, setSearchParams] = useSearchParams()

	const requestTab = (next, { then, onStay } = {}) => {
		const current = userSettingsTab.value
		const go = () => {
			userSettingsTab.value = next
			setSearchParams(searchWith(next), { replace: true })
			then?.()
		}

		if (next === current) return go()

		const guard = settingsLeaveGuards.value[current]
		const count = guard?.count?.() || 0
		if (!count) return go()

		let answered = false
		showToast.confirm(
			`You have ${count} unsaved change${count > 1 ? 's' : ''} in ${guard.label}. Nothing is saved by leaving the tab.`,
			{
				title: 'Leave without saving?',
				tone: 'warning',
				submitText: 'Leave',
				cancelText: 'Stay',
				onSubmit: () => { answered = true; go() },
				onCancel: () => { answered = true; onStay?.() },
				onClose: () => { if (!answered) onStay?.() },
			}
		)
	}

	/** Puts the open tab back in the URL without asking (after *Stay*). */
	const restoreUrl = () => {
		if (userSettingsTab.value) setSearchParams(searchWith(userSettingsTab.value), { replace: true })
	}

	return { requestTab, restoreUrl }
}
